import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Upload, X, Loader2, Send, ArrowLeft, CheckCircle2 } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".jpg,.jpeg,.png,.webp";
const MAX_BYTES = 12 * 1024 * 1024; // 12 MB

function ImageUploadSlot({ label, fileHint, file, onChange, testId }) {
  const inputRef = useRef(null);
  const preview = file ? URL.createObjectURL(file) : null;

  return (
    <div className="border-2 border-dashed border-[#1a365d]/25 hover:border-[#d4af37] transition-colors bg-white">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        data-testid={testId}
        className="hidden"
      />
      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-3 text-[#1a365d] hover:text-[#d4af37] transition-colors"
        >
          <Upload size={28} strokeWidth={1.4} />
          <span className="text-[11px] tracking-[0.28em] uppercase font-medium">
            {label}
          </span>
          <span className="text-[11px] text-[#475569]">{fileHint}</span>
        </button>
      ) : (
        <div className="relative aspect-[4/3]">
          <img
            src={preview}
            alt={label}
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={() => {
              onChange(null);
              if (inputRef.current) inputRef.current.value = "";
            }}
            className="absolute top-3 right-3 bg-white/95 text-[#1a365d] p-1.5 hover:bg-[#d4af37] hover:text-white transition-colors"
            aria-label="Rimuovi"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function InBetweenSubmit() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    project_title: "",
    project_description: "",
    accept_rules: false,
    accept_instagram: false,
  });
  const [imgA, setImgA] = useState(null);
  const [imgB, setImgB] = useState(null);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const set = (key) => (e) => {
    const val =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!imgA || !imgB) {
      setStatus({ state: "error", message: t("inbetweenForm.errBoth") });
      return;
    }
    if (imgA.size > MAX_BYTES || imgB.size > MAX_BYTES) {
      setStatus({ state: "error", message: t("inbetweenForm.errSize") });
      return;
    }
    if (!form.accept_rules || !form.accept_instagram) {
      setStatus({ state: "error", message: t("inbetweenForm.errLegal") });
      return;
    }

    setStatus({ state: "loading", message: "" });
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) =>
        fd.append(k, typeof v === "boolean" ? String(v) : v.trim ? v.trim() : v)
      );
      fd.append("image_a", imgA);
      fd.append("image_b", imgB);
      await axios.post(`${API}/inbetween/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus({
        state: "success",
        message: t("inbetweenForm.successMessage"),
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        country: "",
        city: "",
        project_title: "",
        project_description: "",
        accept_rules: false,
        accept_instagram: false,
      });
      setImgA(null);
      setImgB(null);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || t("inbetweenForm.errGeneric");
      setStatus({ state: "error", message: detail });
    }
  };

  return (
    <PageShell>
      <PageHero
        eyebrow={t("inbetweenForm.eyebrow")}
        title={
          <>
            {t("inbetweenForm.titleA")}{" "}
            <span className="italic">{t("inbetweenForm.titleB")}</span>.
          </>
        }
        subtitle={t("inbetweenForm.subtitle")}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Link
            to="/articles/in-between-2026-05-04"
            data-testid="back-to-inbetween"
            className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.28em] uppercase text-[#475569] hover:text-[#1a365d] mb-12"
          >
            <ArrowLeft size={12} /> {t("inbetweenForm.back")}
          </Link>

          {status.state === "success" ? (
            <div className="bg-[#fafaf7] border-l-2 border-[#d4af37] p-10 md:p-14 text-center">
              <CheckCircle2
                size={48}
                className="mx-auto text-[#d4af37]"
                strokeWidth={1.4}
              />
              <h2 className="font-display text-3xl md:text-4xl text-[#1a365d] mt-6">
                {t("inbetweenForm.successTitle")}
              </h2>
              <p
                data-testid="inbetween-success-message"
                className="mt-5 text-[#475569] leading-relaxed max-w-xl mx-auto"
              >
                {status.message}
              </p>
              <Link to="/" className="btn-outline mt-10">
                {t("inbetweenForm.backHome")}
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-[#fafaf7] p-8 md:p-14 border-l-2 border-[#d4af37] space-y-10"
              data-testid="inbetween-form"
            >
              {/* Project images */}
              <div>
                <span className="eyebrow">{t("inbetweenForm.step1")}</span>
                <h3 className="font-display text-2xl md:text-3xl text-[#1a365d] mt-3 leading-snug">
                  {t("inbetweenForm.step1Title")}
                </h3>
                <p className="mt-3 text-[#475569] text-[14.5px] leading-relaxed max-w-2xl">
                  {t("inbetweenForm.step1Intro")}
                </p>
                <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ImageUploadSlot
                    label={t("inbetweenForm.imageA")}
                    fileHint={t("inbetweenForm.fileHint")}
                    file={imgA}
                    onChange={setImgA}
                    testId="inbetween-upload-a"
                  />
                  <ImageUploadSlot
                    label={t("inbetweenForm.imageB")}
                    fileHint={t("inbetweenForm.fileHint")}
                    file={imgB}
                    onChange={setImgB}
                    testId="inbetween-upload-b"
                  />
                </div>
              </div>

              {/* Personal info */}
              <div className="pt-2">
                <span className="eyebrow">{t("inbetweenForm.step2")}</span>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <label className="block">
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">
                      {t("inbetweenForm.name")}
                    </span>
                    <input
                      type="text"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={set("name")}
                      data-testid="inbetween-name"
                      className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">
                      {t("inbetweenForm.email")}
                    </span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      data-testid="inbetween-email"
                      className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">
                      {t("inbetweenForm.phone")}
                    </span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      data-testid="inbetween-phone"
                      className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">
                      {t("inbetweenForm.country")}
                    </span>
                    <input
                      type="text"
                      required
                      minLength={2}
                      value={form.country}
                      onChange={set("country")}
                      data-testid="inbetween-country"
                      placeholder={t("inbetweenForm.countryPlaceholder")}
                      className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">
                      {t("inbetweenForm.city")}
                    </span>
                    <input
                      type="text"
                      value={form.city}
                      onChange={set("city")}
                      data-testid="inbetween-city"
                      className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body"
                    />
                  </label>
                </div>
              </div>

              {/* Project */}
              <div className="pt-2">
                <span className="eyebrow">{t("inbetweenForm.step3")}</span>
                <div className="mt-6 space-y-7">
                  <label className="block">
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">
                      {t("inbetweenForm.projectTitle")}
                    </span>
                    <input
                      type="text"
                      required
                      minLength={2}
                      value={form.project_title}
                      onChange={set("project_title")}
                      data-testid="inbetween-project-title"
                      className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">
                      {t("inbetweenForm.projectDescription")}
                    </span>
                    <textarea
                      required
                      minLength={10}
                      rows={6}
                      value={form.project_description}
                      onChange={set("project_description")}
                      data-testid="inbetween-project-description"
                      placeholder={t("inbetweenForm.descriptionPlaceholder")}
                      className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body resize-none"
                    />
                  </label>
                </div>
              </div>

              {/* Legal */}
              <div className="pt-2 space-y-4">
                <span className="eyebrow">{t("inbetweenForm.step4")}</span>
                <label className="flex items-start gap-3 mt-4">
                  <input
                    type="checkbox"
                    required
                    checked={form.accept_rules}
                    onChange={set("accept_rules")}
                    data-testid="inbetween-accept-rules"
                    className="mt-1.5 accent-[#1a365d]"
                  />
                  <span className="text-[14px] text-[#1e293b] leading-relaxed">
                    <strong>{t("inbetweenForm.acceptRules1")}</strong>{" "}
                    {t("inbetweenForm.acceptRules2")}{" "}
                    <Link
                      to="/inbetween/regolamento"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="inbetween-regolamento-link"
                      className="font-semibold text-[#1a365d] underline decoration-[#d4af37] decoration-2 underline-offset-4 hover:text-[#d4af37] transition-colors"
                    >
                      IN BETWEEN
                    </Link>{" "}
                    {t("inbetweenForm.acceptRules3")}
                  </span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    required
                    checked={form.accept_instagram}
                    onChange={set("accept_instagram")}
                    data-testid="inbetween-accept-instagram"
                    className="mt-1.5 accent-[#1a365d]"
                  />
                  <span className="text-[14px] text-[#1e293b] leading-relaxed">
                    {t("inbetweenForm.acceptInstagram1")}{" "}
                    <a
                      href="https://www.instagram.com/tesorideuropa/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1a365d] underline hover:text-[#d4af37]"
                    >
                      @tesorideuropa
                    </a>{" "}
                    {t("inbetweenForm.acceptInstagram2")}
                  </span>
                </label>
              </div>

              {/* Submit */}
              <div className="pt-6 border-t border-[#e2e8f0]">
                {status.message && (
                  <p
                    data-testid="inbetween-feedback"
                    className={`mb-5 text-sm font-body ${
                      status.state === "error"
                        ? "text-[#9b1c1c]"
                        : "text-[#1a365d]"
                    }`}
                  >
                    {status.message}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid="inbetween-submit-btn"
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "loading" ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />{" "}
                      {t("inbetweenForm.submitting")}
                    </>
                  ) : (
                    <>
                      {t("inbetweenForm.submit")} <Send size={14} strokeWidth={1.8} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}
